import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BillingPreviewTeamSeat } from '../../types/adminInvoice';
import { formatLastLogin } from '../../utils/admin/formatSubscriptionDate';

interface BillingTeamSeatsHoverProps {
  seats: BillingPreviewTeamSeat[];
  enabled: boolean;
  children: React.ReactNode;
  className?: string;
}

function ActiveBadge({ isActive }: { isActive: boolean | null }) {
  if (isActive === true) {
    return (
      <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-800">
        Active
      </span>
    );
  }
  if (isActive === false) {
    return (
      <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-700">
        Inactive
      </span>
    );
  }
  return null;
}

/**
 * Hover tooltip for seat-locked billing preview — uses team_seats from the preview payload
 * (no extra fetch). Only render with enabled when is_seat_locked / seat_locked sports.
 */
const BillingTeamSeatsHover: React.FC<BillingTeamSeatsHoverProps> = ({
  seats,
  enabled,
  children,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tooltipId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - 420;
    setCoords({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
    });
  };

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleEnter = () => {
    if (!enabled) return;
    clearCloseTimer();
    setOpen(true);
    updatePosition();
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => () => clearCloseTimer(), []);

  if (!enabled) {
    return <span className={className}>{children}</span>;
  }

  const sortedSeats = [...seats].sort((a, b) => {
    const aSeat = a.seat_no ?? Number.POSITIVE_INFINITY;
    const bSeat = b.seat_no ?? Number.POSITIVE_INFINITY;
    if (aSeat !== bSeat) return aSeat - bSeat;
    return a.username.localeCompare(b.username);
  });

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex cursor-help border-b border-dotted border-orange-400 ${className}`.trim()}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
      >
        {children}
      </span>
      {open &&
        coords &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[100] w-[400px] rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleLeave}
          >
            <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-700">
              Team seat members ({sortedSeats.length})
            </div>
            {sortedSeats.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">No seat members returned.</div>
            ) : (
              <div className="max-h-72 overflow-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Username</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Email</th>
                      <th className="px-2 py-1.5 text-center font-medium text-gray-500">Seat #</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Last login</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Logins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSeats.map((seat) => (
                      <tr key={seat.user_id} className="border-t border-gray-100 align-top">
                        <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">
                          {seat.username}
                          <ActiveBadge isActive={seat.is_active} />
                        </td>
                        <td
                          className="px-2 py-1.5 text-gray-600 max-w-[140px] truncate"
                          title={seat.email || undefined}
                        >
                          {seat.email?.trim() ? seat.email : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-center text-gray-700">
                          {seat.seat_no != null ? seat.seat_no : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                          {formatLastLogin(seat.last_login)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-gray-700">{seat.login_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default BillingTeamSeatsHover;
